import React, { useState } from 'react';
import { BomCompleteness } from '../../../types/bom.types';
import { ManualEditRequest } from '../../../types/bomVersions';
import { assessCompleteness, describeCompleteness } from '../../../utils/bomLines';
import useBomSheetDraft from './useBomSheetDraft';
import BomSheetTable from './BomSheetTable';
import BomSheetFooter from './BomSheetFooter';
import { BomSaveFailure, ExtractedBomRow, SheetRow } from './types';
import { EnhanceSelection } from '../../../utils/bomProposal';

/**
 * The body of the extracted-BOM modal: the editable grid plus its footer.
 * Mounted only while the modal is open, so closing it discards the draft.
 *
 * The completeness line reads the server's count while nothing is edited and
 * recounts the draft (same rule) once something is, so it never describes a
 * BOM the buyer has already changed on screen.
 */
export interface ExtractedBomSheetProps {
  components: ExtractedBomRow[];
  completeness?: BomCompleteness | null;
  readOnly: boolean;
  saving: boolean;
  /** Resolves null on success (the caller closes the sheet), or the refusal. */
  onSave: (request: ManualEditRequest, changedCount: number) => Promise<BomSaveFailure | null>;
  onClose: () => void;
  /** "Enhance with Robbie": asks about the whole BOM (no row selection in this sheet yet). */
  onEnhanceWithRobbie?: (selection?: EnhanceSelection) => void;
}

const sentence = (text: string) => text.trim().replace(/[.\s]+$/, '');

const completenessMessage = (completeness: BomCompleteness, readOnly: boolean): string => {
  const attention = describeCompleteness(completeness);
  if (!attention) {
    return completeness.total === 0
      ? 'There are no lines on this BOM yet.'
      : 'Every line has a name, quantity, description and a part number or MPN. Tell me if something still looks wrong.';
  }
  return readOnly ? `${attention}.` : `${attention}. Fill them in here, or ask me to enhance them.`;
};

const blamedRow = (rows: SheetRow[], failure: BomSaveFailure | null): SheetRow | undefined => {
  if (!failure) return undefined;
  if (failure.componentId) return rows.find((r) => r.original.componentId === failure.componentId);
  if (failure.addIndex !== undefined) return rows.filter((r) => r.kind === 'added')[failure.addIndex];
  return undefined;
};

export const ExtractedBomSheet: React.FC<ExtractedBomSheetProps> = ({
  components,
  completeness,
  readOnly,
  saving,
  onSave,
  onClose,
  onEnhanceWithRobbie,
}) => {
  const draft = useBomSheetDraft(components);
  const [failure, setFailure] = useState<BomSaveFailure | null>(null);

  const shownCompleteness =
    !draft.hasChanges && completeness ? completeness : assessCompleteness(draft.remainingLines);

  const blamed = blamedRow(draft.rows, failure);
  const failureText = failure
    ? blamed
      ? `I couldn't save, so nothing changed. Line ${draft.rows.indexOf(blamed) + 1}` +
        ` (${draft.value(blamed, 'name').trim() || 'no name yet'}): ${sentence(failure.message)}. Fix it and save again.`
      : `I couldn't save, so nothing changed: ${sentence(failure.message)}. Try saving again; if it keeps failing, reload the page.`
    : null;

  const save = async () => {
    setFailure(null);
    setFailure(await onSave(draft.request, draft.changedCount));
  };

  return (
    <>
      <BomSheetTable draft={draft} readOnly={readOnly} disabled={saving} blamedKey={blamed?.key ?? null} />
      <BomSheetFooter
        readOnly={readOnly}
        saving={saving}
        message={completenessMessage(shownCompleteness, readOnly)}
        changedCount={draft.changedCount}
        addedCount={draft.request.adds?.length ?? 0}
        removedCount={draft.request.removes?.length ?? 0}
        hasChanges={draft.hasChanges}
        invalidCount={draft.invalidCount}
        failure={failureText}
        onAddLine={draft.addLine}
        // Wrapped: a click handler would hand the event over as the selection.
        onEnhance={onEnhanceWithRobbie ? () => onEnhanceWithRobbie() : undefined}
        onCancel={onClose}
        onSave={save}
      />
    </>
  );
};

export default ExtractedBomSheet;
